/* daisip — theme JS
 *
 * Centralised cart event bus.
 *
 * Any code that mutates the cart should call window.daisipCart.refresh()
 * after the mutation completes. That triggers a single fetch to /cart.js
 * and dispatches a `daisip:cart:updated` event with the new cart payload.
 *
 * Listen with:
 *   document.addEventListener('daisip:cart:updated', e => { ... e.detail (cart) ... })
 */

(function () {
  'use strict';

  var inflight = null;

  function refresh() {
    // De-dupe overlapping refresh calls
    if (inflight) return inflight;

    inflight = fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Update any cart count elements
        document.querySelectorAll('[data-cart-count], #cart-count').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        // Dispatch event for sections that want full cart payload
        document.dispatchEvent(new CustomEvent('daisip:cart:updated', { detail: cart }));
        inflight = null;
        return cart;
      })
      .catch(function (err) {
        console.error('[daisip] cart refresh failed:', err);
        inflight = null;
      });

    return inflight;
  }

  // Expose
  window.daisipCart = { refresh: refresh };

  // Initial sync on page load
  document.addEventListener('DOMContentLoaded', function () {
    refresh();

    // Auto-refresh after any /cart/add form submission (for non-AJAX add forms)
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (form && form.action && form.action.indexOf('/cart/add') !== -1) {
        // The browser will navigate or the page will reload — but if it's
        // an AJAX-handled form, refresh after a tick.
        setTimeout(refresh, 500);
      }
    });
  });
})();

/* ═══════════════════════════════════════════════════════════════════
   FADE-IN ON SCROLL (v1.5.4)
   - Adds .is-visible to elements with .fade-in-up as they enter viewport
   - Only active if user hasn't requested reduced motion
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var sectionsToReveal = [
    'section.collection',
    'section.kitchens',
    'section.story-section',
    'section.ritual',
    'section.final-cta',
    'section.newsletter',
    '.pdp-cross-sell',
    '.search-page'
  ];

  function setup() {
    var els = document.querySelectorAll(sectionsToReveal.join(','));
    if (!els.length) return;

    els.forEach(function (el) { el.classList.add('fade-in-up'); });

    if (!('IntersectionObserver' in window)) {
      // Older browsers — show immediately, no animation
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });

    els.forEach(function (el) { io.observe(el); });

    // Safety net: if for any reason a section never gets revealed within
    // 2 seconds of setup (observer bug, section already past viewport on
    // a short page, etc.) force-reveal everything. Better to skip the
    // animation than to leave content invisible/unclickable-feeling.
    setTimeout(function () {
      els.forEach(function (el) {
        if (!el.classList.contains('is-visible')) {
          el.classList.add('is-visible');
        }
      });
    }, 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();

/* ═══════════════════════════════════════════════════════════════════
   PDP SWIPE CAROUSEL SYNC (v1.5.5)
   - Updates page dots as user swipes
   - Tap a dot to scroll to that slide
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  function init() {
    var track = document.querySelector('.pdp-image-track');
    if (!track) return;
    var dots = document.querySelectorAll('.pdp-page-dot');
    if (!dots.length) return;
    var slides = track.querySelectorAll('.pdp-image-slide');
    if (!slides.length) return;

    function syncDot() {
      var w = track.clientWidth;
      if (!w) return;
      var idx = Math.round(track.scrollLeft / w);
      idx = Math.max(0, Math.min(dots.length - 1, idx));
      dots.forEach(function (d, i) {
        if (i === idx) d.classList.add('is-active');
        else d.classList.remove('is-active');
      });
    }

    // Throttled scroll listener
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          syncDot();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Tap a dot to jump to that slide
    dots.forEach(function (d, i) {
      d.style.cursor = 'pointer';
      d.addEventListener('click', function () {
        var w = track.clientWidth;
        track.scrollTo({ left: i * w, behavior: 'smooth' });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ═══════════════════════════════════════════════════════════════════
   SMOOTH SCROLL TO COLLECTION ON HOME (v1.6.1)
   - When clicking "Shop" / "Shop the collection" link with #collection
     anchor, smooth-scroll instead of jumping
   - Falls through to default browser behaviour if no #collection target
     is present on the page (e.g. on the cart or product pages) so the
     browser can navigate correctly.
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a');
    if (!link) return;

    var href = link.getAttribute('href') || '';
    // Match #collection, /#collection, and /<anything>/#collection variants
    if (!/(?:^|\/)#collection$/.test(href) && href !== '#collection') return;

    var target = document.getElementById('collection');
    // No anchor target on this page? Let the browser navigate normally
    // (the link will route to / and the home page will then jump there).
    if (!target) return;

    e.preventDefault();
    // Use the actual sticky header height instead of a hardcoded 100px
    var header = document.querySelector('.site-header');
    var headerOffset = header ? header.getBoundingClientRect().height + 12 : 100;
    var rect = target.getBoundingClientRect();
    var top = rect.top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: top, behavior: 'smooth' });
  });
})();

/* ═══════════════════════════════════════════════════════════════════
   DEAD-LINK SAFETY NET (v1.6.1)
   - Catches any link with an empty or "#" href that survived to
     production and gracefully redirects it to /collections/all
     instead of letting the click feel "broken".
   - Skips genuinely useful in-page anchors (anything starting with #
     and longer than 1 char), and skips elements explicitly marked
     data-allow-noop.
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a');
    if (!link) return;
    if (link.hasAttribute('data-allow-noop')) return;

    var href = link.getAttribute('href');
    // Only intercept truly dead hrefs ("" or "#" with nothing after).
    var isDead = href === null || href === '' || href === '#';
    if (!isDead) return;

    e.preventDefault();
    window.location.href = '/collections/all';
  });
})();

/* ═══════════════════════════════════════════════════════════════════
   VARIANT PICKER (v1.5.7)
   - Updates hidden variant ID input + price display when user picks variant
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  function init() {
    var dataEl = document.querySelector('[data-variants-json]');
    if (!dataEl) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!variants || !variants.length) return;

    var idInput = document.querySelector('[data-variant-id-input]');
    var priceDisplay = document.querySelector('[data-price-display]');
    var pickers = document.querySelectorAll('[data-variant-picker]');
    if (!pickers.length) return;

    // Tracks current selection per option position
    var selected = {};
    pickers.forEach(function (picker) {
      var activePill = picker.querySelector('.variant-pill.active');
      if (activePill) {
        selected[activePill.dataset.variantOption] = activePill.dataset.variantValue;
      }
    });

    function findVariant() {
      var keys = Object.keys(selected).sort();
      for (var i = 0; i < variants.length; i++) {
        var match = true;
        for (var k = 0; k < keys.length; k++) {
          var pos = parseInt(keys[k]) - 1;
          if (variants[i].options[pos] !== selected[keys[k]]) {
            match = false;
            break;
          }
        }
        if (match) return variants[i];
      }
      return null;
    }

    function update() {
      var v = findVariant();
      if (!v) return;
      if (idInput) idInput.value = v.id;
      if (priceDisplay) priceDisplay.textContent = v.price_money;
      // Update Add to Cart button availability
      var btn = document.querySelector('.btn-add-to-cart');
      if (btn) {
        if (v.available) {
          btn.disabled = false;
          btn.textContent = 'Add to cart';
        } else {
          btn.disabled = true;
          btn.textContent = 'Sold out';
        }
      }
    }

    pickers.forEach(function (picker) {
      picker.addEventListener('click', function (e) {
        var pill = e.target.closest('.variant-pill');
        if (!pill) return;
        var pos = pill.dataset.variantOption;
        var val = pill.dataset.variantValue;
        // Update visual state
        picker.querySelectorAll('.variant-pill').forEach(function (p) {
          p.classList.remove('active');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('active');
        pill.setAttribute('aria-checked', 'true');
        // Update selection
        selected[pos] = val;
        update();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ═══════════════════════════════════════════════════════════════════
   THEME EDITOR LIFECYCLE (v1.6.0)
   - Re-initialize interactive components when merchant edits a section
   - Without this, theme editor preview shows stale state until full reload
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  if (!window.Shopify || !Shopify.designMode) return;
  // Re-bind on section load + select events
  document.addEventListener('shopify:section:load', function () {
    // Trigger DOMContentLoaded-style re-init by dispatching a custom event
    document.dispatchEvent(new Event('daisip:reinit'));
  });
  document.addEventListener('shopify:section:select', function () {
    document.dispatchEvent(new Event('daisip:reinit'));
  });
})();
